import React, { useState, useEffect, useCallback } from 'react'
import { useParams, NavLink, Outlet, Link, useLocation } from 'react-router-dom'
import { controlCenterApi, type AccountDetail as AccountDetailType } from '../../services/controlCenterApi'

export function AccountDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const [account, setAccount] = useState<AccountDetailType | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchAccount = useCallback(async () => {
    if (!id) return
    setIsLoading(true)
    setError('')
    try {
      const data = await controlCenterApi.getAccount(id)
      setAccount(data)
    } catch {
      setError('Failed to load account')
    }
    setIsLoading(false)
  }, [id])

  useEffect(() => {
    fetchAccount()
  }, [fetchAccount])

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-400 text-sm mt-3">Loading account...</span>
      </div>
    )
  }

  if (error || !account) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-lg">
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
          </svg>
          <span className="text-red-300 text-sm">{error || 'Account not found'}</span>
          <button type="button" onClick={fetchAccount} className="ml-auto text-red-400 hover:text-red-300 text-sm underline">
            Retry
          </button>
        </div>
      </div>
    )
  }

  const basePath = `/control_center/accounts/${account.id}`
  const currentPath = location.pathname

  const tabs = [
    { label: 'Overview', path: basePath, exact: true },
    { label: 'Users', path: `${basePath}/users` },
    { label: 'Features', path: `${basePath}/features` },
    { label: 'Website', path: `${basePath}/website` },
    { label: 'Imports', path: `${basePath}/imports` },
    { label: 'Exports', path: `${basePath}/exports` },
    { label: 'Activity', path: `${basePath}/activity` },
    { label: 'Notes', path: `${basePath}/notes` },
  ]

  return (
    <div>
      {/* Account Header */}
      <div className="mb-6">
        <Link
          to="/control_center/accounts"
          className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white transition-colors mb-3"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back to Accounts
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{account.name}</h1>
              <StatusBadge active={account.active} deleted={!!account.deleted_at} />
              <TypeBadge type={account.account_type} />
              {account.is_on_billing_hold && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Billing Hold
                </span>
              )}
            </div>
            <p className="text-slate-400 text-sm mt-1">
              {account.email}
              {account.domain && <span className="ml-3 text-slate-500">{account.domain}</span>}
            </p>
            <p className="text-slate-600 text-xs mt-1 font-mono">
              {account.legacy_id && <span className="text-slate-500 mr-2">#{account.legacy_id}</span>}
              {account.id}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-slate-700 mb-6">
        <nav className="flex gap-1 -mb-px overflow-x-auto">
          {tabs.map(tab => {
            const isActive = tab.exact
              ? currentPath === tab.path
              : currentPath.startsWith(tab.path)
            return (
              <NavLink
                key={tab.path}
                to={tab.path}
                end={tab.exact}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-slate-400 hover:text-white hover:border-slate-600'
                }`}
              >
                {tab.label}
              </NavLink>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <Outlet context={{ account, refreshAccount: fetchAccount }} />
    </div>
  )
}

function StatusBadge({ active, deleted }: { active: boolean; deleted: boolean }): React.ReactElement {
  if (deleted) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-500/15 text-red-400">
        <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
        Deleted
      </span>
    )
  }
  if (!active) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/15 text-yellow-400">
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
        Inactive
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-500/15 text-green-400">
      <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
      Active
    </span>
  )
}

function TypeBadge({ type }: { type: 'group' | 'rooftop' }): React.ReactElement {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
      type === 'group'
        ? 'bg-blue-500/15 text-blue-400'
        : 'bg-slate-600/50 text-slate-300'
    }`}>
      {type === 'group' ? 'Group' : 'Rooftop'}
    </span>
  )
}
